import PaginatedTable from '../../components/ui/PaginatedTable';
import PermissionButton from "../../components/ui/PermissionButton";
import TableActions from '../../components/ui/TableActions';
import { useState, useEffect } from "react";
import DashboardLayout from "../Layouts/DashboardLayout";
import { MapPin, Package, Users, Clock, Search, Eye } from "lucide-react";
import PODetails, { money } from '../Components/PODetails';
import { useAuth } from '../Context/AuthContext';
import QuotationDetails from './QuotationDetails';

export default function QuotationComparison() {
  const { user, permissions = [] } = useAuth();
  const canFinalize = user?.role === 'super_admin' || permissions.includes('purchase_orders.approve');
  const [poDetails, setPODetails] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [pos, setPos] = useState([]);
  const [selectedPO, setSelectedPO] = useState(null);

  const [quotes, setQuotes] = useState([]);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [previewQuote, setPreviewQuote] = useState(null);

  // ✅ FETCH POs
  useEffect(() => {
    fetch("/api/admin/purchase-orders", {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          setPos(data.data.filter(po=>po.po_type!=='direct'));
        }
      }).catch(() => setError('Unable to load purchase orders. Please refresh.'));
  }, []);

  useEffect(() => {
    if (!selectedPO) return;

    let active = true;
    setQuotes([]); setSelectedQuote(null); setPODetails(null); setError('');
    Promise.all([
      fetch(`/api/admin/quotations/${selectedPO.id}`, { credentials: 'include' }),
      fetch(`/api/purchase-orders/${selectedPO.id}`, { credentials: 'include' }),
    ]).then(async responses => {
      const results = await Promise.all(responses.map(r => r.json()));
      if (!active) return;
      if (results.some((r,i) => !responses[i].ok || r.status !== 'success')) throw new Error('Unable to load PO and quotation details');
      setQuotes(results[0].data); setPODetails(results[1].data);
      setSelectedQuote(results[0].data.find(q => q.status === 'Approved') || null);
    }).catch(e => { if (active) setError(e.message); });
    return () => { active = false; };
  }, [selectedPO]);

  const loadQuotations = async (poId) => {
    const res = await fetch(`/api/admin/quotations/${poId}`, {
      credentials: "include",
    });

    const data = await res.json();

    if (data.status === "success") {
      setQuotes(data.data);

      const approved = data.data.find((q) => q.status === "Approved");

      if (approved) {
        setSelectedQuote(approved);
      } else {
        setSelectedQuote(null);
      }
    }
  };

  // ✅ TOTALS (same logic)
  const totals = quotes.map((q) => Number(q.price || 0));

  const cheapestIndex = totals.indexOf(Math.min(...totals));

  const fastestIndex = quotes
    .map((q) => q.deliveryDays == null ? Infinity : Number(q.deliveryDays))
    .indexOf(
      Math.min(...quotes.map((q) => q.deliveryDays == null ? Infinity : Number(q.deliveryDays))),
    );

  // ✅ FINALIZE
  const finalizeOrder = async () => {
    if (busy || !canFinalize || poDetails?.send_status !== 'Sent') return;
    if (!selectedQuote) {
      alert("Please select a vendor first.");
      return;
    }

    if (
      !window.confirm(
        `Finalize ${selectedQuote.vendor_name}? This cannot be undone.`,
      )
    ) {
      return;
    }

    setBusy(true);
    try {
    const res = await fetch("/api/admin/finalize-quotation", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        po_id: selectedPO.id,
        vendor_id: selectedQuote.vendor_id,
      }),
    });

    const data = await res.json();

    if (data.status === "success") {
      await loadQuotations(selectedPO.id);

      alert("Vendor finalized successfully.");
    } else {
      setError(data.message || data.detail || 'Finalization failed');
    }
    } catch (e) { setError('Unable to finalize quotation. Please retry.'); }
    finally { setBusy(false); }
  };

  const isPOFinalized = quotes.some((q) => q.status === "Approved");
  const filteredPOs = pos.filter(po =>
    [po.po_number, po.location].some(value => String(value || '').toLowerCase().includes(search.trim().toLowerCase())) &&
    (statusFilter === 'All' || po.status === statusFilter));

  return (
    <DashboardLayout>
      <div className="space-y-8">
        {error && <p role="alert" className="rounded-xl bg-red-500/10 border border-red-500/30 p-4 text-red-400">{error}</p>}
        {/* ================== PO LIST ================== */}
        {!selectedPO && (
          <>
            <h1 className="text-2xl font-serif">Vendor Quotations</h1>
            <p className="text-gray-400 text-sm">Open a purchase order to review requirements, item-wise offers and vendor totals.</p>
            <div className="grid md:grid-cols-3 gap-6">{[['Purchase Orders',pos.length],['Finalized',pos.filter(po => po.status === 'Approved').length],['Awaiting Finalization',pos.filter(po => po.status !== 'Approved').length]].map(([label,count]) => <div key={label} className="rounded-2xl bg-white/5 border border-white/10 p-6"><p className="text-sm text-gray-400">{label}</p><p className="text-3xl font-bold mt-2">{count}</p></div>)}</div>
            <div className="flex flex-col md:flex-row gap-4"><div className="relative flex-1"><Search size={18} className="absolute left-3 top-3 text-gray-400" /><input aria-label="Search quotation purchase orders" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search PO / Branch" className="w-full pl-10 pr-4 py-2 rounded-xl bg-white/5 border border-white/10" /></div><select aria-label="Purchase order status" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-2 rounded-xl bg-[#0b0b0f] border border-white/10"><option value="All">All Statuses</option>{[...new Set(pos.map(po => po.status).filter(Boolean))].map(status => <option key={status}>{status}</option>)}</select></div>
            {!pos.length && <p className="text-gray-400">No purchase orders available.</p>}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredPOs.map((po) => (
                <div
                  key={po.id}
                  className="rounded-2xl bg-white/5 border border-white/10 p-6 hover:border-amber-400/40 transition"
                >
                  {/* TOP */}
                  <div className="flex justify-between items-center">
                    <p className="font-medium text-lg">{po.po_number}</p>

                    <span
                      className={`px-3 py-1 rounded-full text-xs ${
                        po.status === "Approved"
                          ? "bg-emerald-500/20 text-emerald-400"
                          : po.status === "Pending Approval"
                            ? "bg-blue-500/20 text-blue-400"
                            : "bg-amber-400/20 text-amber-400"
                      }`}
                    >
                      {po.status}
                    </span>
                  </div>

                  {/* BRANCH */}
                  <div className="flex items-center gap-2 text-xs text-gray-400 mt-2">
                    <MapPin size={14} />
                    <span>{po.location || "—"}</span>
                  </div>

                  {/* DETAILS */}
                  <div className="mt-4 text-sm text-gray-300 space-y-2">
                    <div className="flex items-center gap-2">
                      <Package size={14} />
                      <span>
                        Delivery:{" "}
                        {po.delivery_date
                          ? new Date(po.delivery_date).toLocaleDateString()
                          : "—"}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <Users size={14} />
                      <span>Vendors: {po.vendors || 0}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <Clock size={14} />
                      <span>
                        Created:{" "}
                        {po.created_at
                          ? new Date(po.created_at).toLocaleDateString()
                          : "—"}
                      </span>
                    </div>
                  </div>
                  <PermissionButton permissionModule="purchase_orders" onClick={() => setSelectedPO(po)} className="mt-5 flex items-center gap-2 px-4 py-2 rounded-xl border border-amber-400/30 text-amber-400 hover:bg-amber-400/10"><Eye size={16} />View PO & quotations</PermissionButton>
                </div>
              ))}
              {pos.length > 0 && !filteredPOs.length && <p className="text-gray-400 p-6">No purchase orders match your filters.</p>}
            </div>
          </>
        )}

        {/* ================== QUOTATION TABLE ================== */}
        {selectedPO && (
          <>
            <div className="flex justify-between items-center">
              <h1 className="text-2xl font-serif">
                Quotations – {selectedPO.po_number}
              </h1>

              <PermissionButton permissionModule="purchase_orders"
                onClick={() => {
                  setSelectedPO(null);
                  setQuotes([]);
                  setSelectedQuote(null);
                }}
                className="px-4 py-2 border border-white/20 rounded-lg"
              >
                Back
              </PermissionButton>
            </div>

            {poDetails ? <div className="rounded-2xl border border-white/10 bg-white/5 p-5"><PODetails po={poDetails} /></div> : !error && <p className="text-gray-400">Loading PO details…</p>}
            <div className="rounded-2xl bg-white/5 border border-white/10 overflow-x-auto">
              <PaginatedTable className="w-full text-sm">
                <thead className="bg-black/40 text-gray-400">
                  <tr>
                    <th className="px-6 py-4 text-left">Vendor</th>
                    <th className="px-6 py-4">Quoted Items</th>
                    <th className="px-6 py-4">Delivery Days</th>
                    <th className="px-6 py-4">Total</th>
                    <th className="px-6 py-4">Tags</th>
                    <th className="px-6 py-4">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {quotes.map((q, i) => {
                    const total = totals[i];
                    const isSelected = selectedQuote?.id === q.id;
                    const isFinalized = q.status === "Approved";

                    return (
                      <tr
                        key={q.id}
                        className={`border-t border-white/10 ${
                          isSelected ? "bg-amber-400/10" : "hover:bg-white/5"
                        }`}
                      >
                        <td className="px-6 py-4 font-medium">
                          {q.vendor_name}

                          {q.status && (
                            <span
                              className={`inline-block mt-2 px-2 py-1 rounded-full text-xs ${
                                q.status === "Approved"
                                  ? "bg-emerald-500/20 text-emerald-400"
                                  : q.status === "Rejected"
                                    ? "bg-red-500/20 text-red-400"
                                    : "bg-yellow-500/20 text-yellow-400"
                              }`}
                            >
                              {q.status}
                            </span>
                          )}
                        </td>

                        <td className="px-6 py-4 text-center">{q.items?.length || 0}</td>

                        <td className="px-6 py-4 text-center">
                          {q.deliveryDays}
                        </td>

                        <td className="px-6 py-4 text-center">{money(total)}</td>

                        <td className="px-6 py-4 text-center space-x-2">
                          {i === cheapestIndex && (
                            <span className="px-2 py-1 rounded-full text-xs bg-emerald-500/20 text-emerald-400">
                              Cheapest
                            </span>
                          )}
                          {i === fastestIndex && (
                            <span className="px-2 py-1 rounded-full text-xs bg-blue-500/20 text-blue-400">
                              Fastest
                            </span>
                          )}
                          {q.status === "Approved" && (
                            <span className="px-2 py-1 rounded-full text-xs bg-emerald-500/20 text-emerald-400">
                              ✔ Approved
                            </span>
                          )}

                          {q.status === "Rejected" && (
                            <span className="px-2 py-1 rounded-full text-xs bg-red-500/20 text-red-400">
                              ✖ Rejected
                            </span>
                          )}
                        </td>

                        <td className="px-6 py-4 text-center space-x-2"><TableActions>
                          <PermissionButton permissionModule="purchase_orders"
                            onClick={() => setPreviewQuote(q)}
                            className="px-3 py-1 rounded-lg border border-white/20 hover:bg-white/10"
                          >
                            <span className="flex items-center gap-2"><Eye size={16} />View vendor & PO details</span>
                          </PermissionButton>

                          <PermissionButton permissionModule="purchase_orders"
                            disabled={isPOFinalized || !canFinalize || poDetails?.send_status !== 'Sent' || busy}
                            onClick={() => setSelectedQuote(q)}
                            className={`px-3 py-1 rounded-lg transition ${
                              isSelected
                                ? "bg-amber-400 text-black"
                                : isPOFinalized
                                  ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                                  : "border border-white/20 hover:bg-white/10"
                            }`}
                          >
                            Select
                          </PermissionButton>
                        </TableActions></td>
                      </tr>
                    );
                  })}
                  {!quotes.length && <tr><td colSpan={6} className="p-8 text-center text-gray-400">No vendor quotations submitted for this PO.</td></tr>}
                </tbody>
              </PaginatedTable>
            </div>

            <div className="flex justify-end">
              <PermissionButton permissionModule="purchase_orders"
                disabled={isPOFinalized || !canFinalize || !selectedQuote || poDetails?.send_status !== 'Sent' || busy}
                onClick={finalizeOrder}
                className={`px-6 py-3 rounded-xl font-medium transition ${
                  isPOFinalized
                    ? "bg-gray-600 text-gray-300 cursor-not-allowed"
                    : "bg-amber-400 text-black hover:bg-amber-300"
                }`}
              >
                {isPOFinalized
                  ? "Already Finalized"
                  : busy ? 'Finalizing…' : !canFinalize ? 'Approval permission required' : poDetails?.send_status !== 'Sent' ? 'PO must be sent first' : "Finalize Selected Vendor"}
              </PermissionButton>
            </div>
          </>
        )}

        {/* ================== MODAL ================== */}
        {previewQuote && (
          <Modal onClose={() => setPreviewQuote(null)}>
            <h2 className="text-xl font-serif mb-4">
              Quotation – {previewQuote.vendor_name}
            </h2>

            <QuotationDetails quote={previewQuote} po={poDetails} />

            <div className="flex justify-end mt-6">
              <PermissionButton permissionModule="purchase_orders"
                onClick={() => setPreviewQuote(null)}
                className="px-5 py-2 rounded-xl bg-amber-400 text-black"
              >
                Close
              </PermissionButton>
            </div>
          </Modal>
        )}
      </div>
    </DashboardLayout>
  );
}

function Modal({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center">
      <div role="dialog" aria-modal="true" aria-label="Vendor quotation details" className="w-full max-w-6xl max-h-[90vh] overflow-y-auto rounded-2xl bg-[#0b0b0f] border border-white/10 p-8 relative">
        <PermissionButton permissionModule="purchase_orders"
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white"
        >
          ✕
        </PermissionButton>
        {children}
      </div>
    </div>
  );
}
